import React from "react";

const ReturnRefundPolicy = () => {
  return (
    <div className="bg-gray-50 dark:bg-gray-950 min-h-screen py-16 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-4xl mx-auto bg-white dark:bg-gray-900 shadow-xl rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
        {/* Premium Header */}
        <header className="bg-slate-900 dark:bg-slate-800 py-12 px-8 text-center text-white border-b dark:border-slate-700">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight uppercase border-b-4 border-white inline-block pb-2 mb-4">
            Return & Refund Policy
          </h1>
          <p className="text-lg font-semibold text-slate-300">
           Brightex Solutions Pvt Ltd
          </p>
          <p className="text-sm text-slate-400 mt-2 tracking-widest uppercase">
            Effective Date: February 2026
          </p>
        </header>

        {/* Content Body */}
        <div className="p-8 md:p-14 space-y-12 text-gray-700 dark:text-gray-300 leading-relaxed">
          {/* Section 1 */}
          <section>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 border-l-4 border-slate-900 dark:border-slate-100 pl-4">
              1. INTRODUCTION
            </h3>
            <div className="space-y-4 text-gray-600 dark:text-gray-400">
              <p>
                At Brightex Solutions Private Limited (operating as lapzop.com),
                your satisfaction is our priority.
              </p>
              <p>
                This policy outlines the conditions, procedures, and eligibility
                criteria for returns and refunds to ensure a transparent
                experience for our customers purchasing refurbished laptops.
              </p>
            </div>
          </section>

          {/* Section 2 - Sub-points with unique styling */}
          <section className="bg-orange-50 dark:bg-orange-950/30 p-6 rounded-xl border border-orange-100 dark:border-orange-900">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6 border-l-4 border-slate-900 dark:border-slate-100 pl-4">
              2. ELIGIBILITY CRITERIA
            </h3>
            <ul className="space-y-5">
              <li className="flex gap-4 items-start">
                <span className="flex-shrink-0 bg-slate-900 dark:bg-slate-700 text-white w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold">
                  A
                </span>
                <p>
                  <strong className="text-slate-900 dark:text-slate-200">Product Condition:</strong>{" "}
                  Products must be returned in their original condition, unused,
                  and with all original packaging, chargers, and accessories
                  intact.
                </p>
              </li>
              <li className="flex gap-4 items-start">
                <span className="flex-shrink-0 bg-slate-900 dark:bg-slate-700 text-white w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold">
                  B
                </span>
                <p>
                  <strong className="text-slate-900 dark:text-slate-200">Time Frame:</strong> Return
                  or refund requests must be submitted within 7 days from the
                  date of delivery.
                </p>
              </li>
              <li className="flex gap-4 items-start">
                <span className="flex-shrink-0 bg-slate-900 dark:bg-slate-700 text-white w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold">
                  C
                </span>
                <p>
                  <strong className="text-slate-900 dark:text-slate-200">Proof of Purchase:</strong>{" "}
                  A valid invoice or order number from lapzop.com is required
                  for all requests.
                </p>
              </li>
              <li className="flex gap-4 items-start">
                <span className="flex-shrink-0 bg-slate-900 dark:bg-slate-700 text-white w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold">
                  D
                </span>
                <p>
                  <strong className="text-slate-900 dark:text-slate-200">Valid Reasons:</strong>{" "}
                  Refunds are considered for defective/damaged products,
                  incorrect items received, or products not meeting functional
                  expectations.
                </p>
              </li>
            </ul>
          </section>

          {/* Section 3 - Numbered Sub-points */}
          <section>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6 border-l-4 border-slate-900 dark:border-slate-100 pl-4">
              3. REFUND PROCESS
            </h3>
            <div className="grid grid-cols-1 gap-6">
              {[
                {
                  title: "Initiating a Request",
                  desc: "Contact our support via email or phone within the 7-day period, providing your order number and the reason for the request.",
                },
                {
                  title: "Evaluation",
                  desc: "Our team will review your request; we may require photos or an unboxing video to assess the condition.",
                },
                {
                  title: "Product Return",
                  desc: "Once authorized, the product must be sent back to our Bengaluru facility. Refunds are processed after physical inspection.",
                },
                {
                  title: "Notification",
                  desc: "You will be notified via email or phone regarding the approval or rejection of your refund status.",
                },
                {
                  title: "Refund Method",
                  desc: "Approved refunds are issued via original payment. It may take up to 2 business days to reflect in your account.",
                },
              ].map((step, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm hover:border-slate-300 dark:hover:border-gray-500 transition-all"
                >
                  <div className="bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-extrabold text-lg border-2 border-slate-900 dark:border-slate-600">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-slate-200 uppercase text-sm tracking-wide mb-1">
                      {step.title}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 4 */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900 rounded-xl">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 border-l-4 border-slate-900 dark:border-slate-100 pl-3">
                4. FULL REFUND
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-300 italic mb-2 font-medium">
                Issued when products fully meet eligibility criteria.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                Covers total amount paid excluding specific shipping charges.
              </p>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 border-l-4 border-slate-900 dark:border-slate-100 pl-3">
                PARTIAL REFUND
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 italic mb-2 font-medium">
                If products are used, altered, or missing minor accessories.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed text-justify">
                The amount is adjusted based on the item's condition.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 border-l-4 border-slate-900 dark:border-slate-100 pl-4">
              5. LEGAL TERMS
            </h3>
            <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
              <p>
                <strong className="text-slate-900 dark:text-slate-200">• Policy Acceptance:</strong>{" "}
                By purchasing from lapzop.com, you agree to these terms.
              </p>
              <p>
                <strong className="text-slate-900 dark:text-slate-200">
                  • Limitation of Liability:
                </strong>{" "}
                Brightex Solutions Private Limited is not liable for indirect
                damages, including lost data.
              </p>
              <p>
                <strong className="text-slate-900 dark:text-slate-200">
                  • Dispute Resolution:
                </strong>{" "}
                Governed by the laws of India. Jurisdiction: Bengaluru,
                Karnataka.
              </p>
              <p>
                <strong className="text-slate-900 dark:text-slate-200">• Severability:</strong> If
                any part is invalid, remaining provisions remain in effect.
              </p>
            </div>
          </section>

          {/* Section 6 - Contact */}
          <footer className="mt-12 p-8 bg-slate-900 dark:bg-slate-800 rounded-2xl text-white">
            <h3 className="text-xl font-bold mb-6 border-l-4 border-white pl-4 uppercase tracking-widest">
              6. CONTACT US
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2 text-slate-300">
                <p className="font-bold text-white text-lg tracking-tight">
                 Brightex Solutions Pvt Ltd
                </p>
                <p className="text-sm leading-relaxed max-w-sm">
                  No. 5 and 6, Krishnappa Compound, Jaraganahally, JP Nagar 7th
                  Phase, Bengaluru, Karnataka, 560078
                </p>
              </div>
              <div className="space-y-3 text-sm text-slate-300">
                <div className="flex justify-between border-b border-slate-700 pb-2">
                  <span className="font-bold text-white uppercase tracking-tighter">
                    GSTIN
                  </span>
                  <span className="font-mono">29AAICB6992P1ZL</span>
                </div>
                <div className="flex justify-between border-b border-slate-700 pb-2">
                  <span className="font-bold text-white uppercase tracking-tighter">
                    Phone
                  </span>
                  <span>+91 9353035776</span>
                </div>
                <div className="pt-2 flex flex-wrap gap-3">
                  <a
                    href="mailto:Jplapzop@gmail.com"
                    className="inline-block bg-white text-slate-900 px-6 py-2 rounded-full font-bold hover:bg-slate-200 transition-colors"
                  >
                    Jplapzop@gmail.com
                  </a>
                  <a
                    href="mailto:btxinwardoutward@gmail.com"
                    className="inline-block bg-white text-slate-900 px-6 py-2 rounded-full font-bold hover:bg-slate-200 transition-colors"
                  >
                    btxinwardoutward@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default ReturnRefundPolicy;
